import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import {
  createProperty,
  buildAddRoomPayload,
  addRoomTypeForHotel,
  uploadHotelImages,
  getDecryptedToken,
} from '../../api/onboardingApi';
import { MIN_MAIN_IMAGES, MIN_PROPERTY_IMAGES } from './onboardingConfig';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

/** If setHotels returns a token, store it the same way signup does (does not remove signup flow). */
function persistSetHotelsTokenIfPresent(token) {
  if (!token) return;
  try {
    const encrypted = CryptoJS.AES.encrypt(String(token), SECRET_KEY).toString();
    Cookies.set(TOKEN_COOKIE_KEY, encrypted, { secure: true, sameSite: 'strict' });
  } catch (_) {
    /* ignore */
  }
}

function sectionFileCount(imagesBySection, key) {
  const list = imagesBySection?.[key];
  return Array.isArray(list) ? list.filter(Boolean).length : 0;
}

function countFiles(imagesBySection) {
  if (!imagesBySection || typeof imagesBySection !== 'object') return 0;
  return Object.values(imagesBySection).reduce(
    (sum, files) => sum + (Array.isArray(files) ? files.filter(Boolean).length : 0),
    0
  );
}

async function safeRefetch(refetch) {
  try {
    await refetch?.(true);
  } catch (_) {
    /* ignore */
  }
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function validateRequiredImages(imagesBySection) {
  const mainCount = sectionFileCount(imagesBySection, 'main_image');
  const propCount = sectionFileCount(imagesBySection, 'hotel_images');
  if (mainCount < MIN_MAIN_IMAGES) {
    return `Add at least ${MIN_MAIN_IMAGES} main image before submitting.`;
  }
  if (propCount < MIN_PROPERTY_IMAGES) {
    return `Add at least ${MIN_PROPERTY_IMAGES} property images before submitting.`;
  }
  return null;
}

/**
 * Create the property (unless retrying), add room types, then upload images.
 * Listing is only successful when required images upload. If create already
 * happened and images fail, returns hotel_id so the UI can retry upload
 * without calling setHotels again.
 */
export async function submitOnboarding({
  payload,
  rooms,
  imagesBySection,
  onImageProgress,
  refetch,
  successToast,
  errorToast,
  handleInvalidToken,
  mode = 'signup',
  authToken = null,
  /** When set, skip setHotels and only finish rooms + images (image-failure retry). */
  existingHotelId = null,
}) {
  const imageGate = validateRequiredImages(imagesBySection);
  if (imageGate) {
    errorToast(imageGate);
    return { ok: false, isAuthError: false, message: imageGate, imagesUploaded: false };
  }

  const imageCount = countFiles(imagesBySection);
  const roomList = Array.isArray(rooms) ? rooms : [];

  let newHotelId = existingHotelId ? String(existingHotelId) : null;
  let setHotelsToken = authToken || getDecryptedToken();

  if (!newHotelId) {
    const result = await createProperty(payload, { token: authToken });

    if (!result.ok) {
      if (result.isAuthError) {
        handleInvalidToken?.();
        return { ok: false, isAuthError: true };
      }
      errorToast(result.message || 'Failed to create property');
      return { ok: false, isAuthError: false, message: result.message };
    }

    newHotelId = result.hotel_id ? String(result.hotel_id) : null;
    setHotelsToken = result.token || authToken || getDecryptedToken();

    if (!newHotelId) {
      errorToast('Hotel created but hotel_id missing from response.');
      return { ok: false, isAuthError: false, message: 'Missing hotel_id' };
    }

    localStorage.setItem('selectedHotelId', String(newHotelId));
    if (payload.hotel_name) {
      localStorage.setItem('selectedHotelName', String(payload.hotel_name));
    }

    persistSetHotelsTokenIfPresent(setHotelsToken);

    window.dispatchEvent(new StorageEvent('storage', { key: 'selectedHotelId', newValue: String(newHotelId) }));
    window.dispatchEvent(new Event('hotelsUpdated'));
  } else {
    localStorage.setItem('selectedHotelId', String(newHotelId));
    if (payload?.hotel_name) {
      localStorage.setItem('selectedHotelName', String(payload.hotel_name));
    }
  }

  if (!setHotelsToken) {
    const message =
      `Property was created (ID ${newHotelId}) but images could not be uploaded — no auth token. ` +
      'Stay on this page and try again after logging in, or add images from Dashboard → Property Images.';
    errorToast(message);
    await safeRefetch(refetch);
    return {
      ok: false,
      isAuthError: false,
      hotel_id: newHotelId,
      message,
      imagesUploaded: false,
      imageCount,
      createdWithoutImages: true,
    };
  }

  const roomsFailed = [];
  let roomsAdded = 0;
  // On image-failure retry, skip room adds to avoid duplicate approval requests.
  if (!existingHotelId) {
    for (const room of roomList) {
      const roomPayload = buildAddRoomPayload({ token: setHotelsToken, hotelId: newHotelId, state: room });
      // eslint-disable-next-line no-await-in-loop
      const roomResult = await addRoomTypeForHotel(roomPayload);
      if (roomResult.ok) {
        roomsAdded += 1;
      } else {
        roomsFailed.push({ name: room.roomTypeName || 'Room type', message: roomResult.message });
      }
    }
  }

  let imageResult = await uploadHotelImages({
    token: setHotelsToken,
    hotelId: newHotelId,
    imagesBySection,
    onProgress: onImageProgress,
  });
  // One automatic retry for flaky uploads before failing the listing.
  if (!imageResult.ok) {
    imageResult = await uploadHotelImages({
      token: setHotelsToken,
      hotelId: newHotelId,
      imagesBySection,
      onProgress: onImageProgress,
    });
  }

  if (!imageResult.ok) {
    const detail = imageResult.message ? ` (${imageResult.message})` : '';
    const message =
      `Property was created (ID ${newHotelId}) but images failed to upload${detail}. ` +
      'Click Submit again to retry the upload — the property will not be created twice.';
    errorToast(message);
    await safeRefetch(refetch);
    return {
      ok: false,
      isAuthError: !!imageResult.isAuthError,
      hotel_id: newHotelId,
      message,
      roomsAdded,
      roomsFailed,
      imagesUploaded: false,
      imageCount,
      createdWithoutImages: true,
    };
  }

  if (roomsFailed.length > 0) {
    const names = roomsFailed.map((f) => f.name).join(', ');
    const detail = roomsFailed[0]?.message ? ` (${roomsFailed[0].message})` : '';
    errorToast(
      `${roomsFailed.length} of ${plural(roomList.length, 'room type')} could not be added: ${names}${detail}. Add them from Room Types.`
    );
  }

  const created = ['Property', plural(imageCount, 'image')];
  if (roomsAdded > 0) created.splice(1, 0, plural(roomsAdded, 'room type'));
  const list =
    created.length > 1
      ? `${created.slice(0, -1).join(', ')} and ${created[created.length - 1]}`
      : created[0];
  successToast(`${list} created successfully!`);

  await safeRefetch(refetch);
  return {
    ok: true,
    hotel_id: newHotelId,
    roomsAdded,
    roomsFailed,
    imagesUploaded: true,
    imageCount,
  };
}
